import { PlanSubmissionStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import type { PlanTypeCardSubmissionError } from '@ebalance/proto/bffuser/v1/submission_management_model_pb'

import {
  ERROR_CATEGORY_LABEL_KEY,
  categoryInterpolatedMessage,
  resolvePlanErrorRow,
} from '~/models/planSubmission/errorDetail'
import type { InterpolatedMessage } from '~/models/planSubmission/interpolatedMessage'
import {
  SEND_FAILED_CAUSE_SHORT_KEY,
  resolveSendFailedCardText,
  resolveSendFailedCause,
} from '~/models/planSubmission/sendFailedCause'
import { deriveStatusBadge } from '~/models/planSubmission/statusBadge'

/**
 * 1 行の中の 1 メッセージ。`text` は既に確定済みの文字列 (受付エラー)、
 * `i18nKey` は呼び出し元 component が `t()` で解決する key (分類 / 汎用対処文)、
 * `sendFailedCause` は送信失敗の「[短い区別] 1 文」 (AC-PSM-053)。
 * i18n 解決を component 側に残すのは `models/planSubmission/errorDetail.ts` の
 * 既存分離方針 (model は純関数、`t()` は呼び出し元) を踏襲するため。
 */
export type CardErrorRowMessage =
  | { kind: 'text'; text: string }
  | { kind: 'i18nKey'; message: InterpolatedMessage }
  | {
      kind: 'sendFailedCause'
      /** 短い区別 (通信障害 / 回数上限 / 認証情報) の key。 */
      shortKey: string
      /** 1 文 (拒否は応答コード入り)。 */
      text: InterpolatedMessage
    }

export type CardErrorRow = {
  /** React key。BG コード + 断面 + index (同一 BG コード + 断面の重複は契約上無い想定だが index で必ず一意にする)。 */
  key: string
  /** カード内計画レコードが 2 件以上の時だけ前置する代表 BG コード (Decision 6)。 */
  bgCodePrefix: string | undefined
  messages: CardErrorRowMessage[]
}

/**
 * ヘッダー内エラー行を導出する純関数 (Decision 6, Override 表 2 行目)。
 *
 * `errors` (= エラー状態の代表提出計画) 1 件につき 1 行。
 *
 * - 送信失敗 (`SEND_FAILED`): 原因の短い区別と 1 文を出す (AC-PSM-053。
 *   プロト ebalance-prototype #468 の `SEND_FAIL_CAUSES.short` / `card`)。
 *   原因は backend が `error_category` を 4 区分に正規化して返すので、
 *   frontend は写像するだけ (`models/planSubmission/sendFailedCause.ts`)。
 * - それ以外: 一覧 `PlanSubmitErrorIndicator` と同じ `resolvePlanErrorRow`
 *   (`models/planSubmission/errorDetail.ts`: reception_errors 全件 →
 *   error_category → variant 別の汎用 fallback) を共有する。
 *
 * `planRecordCount` (カード内の計画レコード数) が 2 件以上の時だけ、行の代表
 * BG コードを前置する (Decision 6 後半)。
 */
export function buildCardErrorRows(
  errors: readonly PlanTypeCardSubmissionError[],
  planRecordCount: number,
): CardErrorRow[] {
  return errors.map((error, index) => ({
    key: `${error.representativeBgCode}-${error.timeframe}-${index}`,
    bgCodePrefix: planRecordCount >= 2 ? error.representativeBgCode : undefined,
    messages: resolveCardErrorMessages(error),
  }))
}

function resolveCardErrorMessages(
  error: PlanTypeCardSubmissionError,
): CardErrorRowMessage[] {
  if (error.status === PlanSubmissionStatus.SEND_FAILED) {
    const cause = resolveSendFailedCause(error.errorCategory)
    return [
      {
        kind: 'sendFailedCause',
        shortKey: SEND_FAILED_CAUSE_SHORT_KEY[cause],
        text: resolveSendFailedCardText(
          cause,
          error.credentialRejectionResultCodes,
        ),
      },
    ]
  }
  const { variant } = deriveStatusBadge(error.status)
  const resolvedRow = resolvePlanErrorRow(
    {
      errorCategory: error.errorCategory,
      receptionErrors: error.receptionErrors,
      breakdownRowLimit: error.breakdownRowLimit,
    },
    variant,
  )
  if (resolvedRow.kind === 'fallback') {
    return [{ kind: 'i18nKey', message: { key: resolvedRow.fallbackKey } }]
  }
  return resolvedRow.messages.map((message) =>
    message.kind === 'receptionError'
      ? { kind: 'text', text: message.text }
      : {
          kind: 'i18nKey',
          message: categoryInterpolatedMessage(
            ERROR_CATEGORY_LABEL_KEY,
            message,
          ),
        },
  )
}
